import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getMenuItemNamesColor } from 'styles/helpers/getThemeStyles';
import GroupOrderSpendingOption from 'bento-ordering/group_ordering/components/GroupOrderSpendingOption';
import {
  SPENDING_LIMIT_BUDGET_STRING,
  SPENIDNG_LIMIT_TITLE
} from 'bento-ordering/group_ordering/constants/groupOrder.js';
import { useSelector } from 'react-redux';
import { selectSpendingLimitPresets } from 'bento-ordering/cart/selectors/index';
import { formatPresetSpendingLimits } from 'bento-ordering/group_ordering/utils';

const TitleTypography = styled(Typography)(({ theme }) => ({
  color: getMenuItemNamesColor(theme),
  fontSize: '14px',
  fontWeight: '500'
}));

const TitleWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap'
});

const SpendingLimitContainer = styled(Box)({
  marginBottom: '.5rem'
});

const BudgetTypography = styled(Typography)({
  marginTop: '.5rem',
  marginBottom: '.5rem'
});

const CustomInput = styled(TextField)(({ theme }) => ({
  color: getMenuItemNamesColor(theme),
  fontSize: '16px'
}));

const DollarIcon = styled(InputAdornment)(({ theme }) => ({
  color: getMenuItemNamesColor(theme)
}));

const CUSTOM_VALUE = 'custom';

const GroupOrderSpendingLimit = ({
  onSpendingLimitChange,
  isCustomLimitValid,
  customLimit,
  setCustomLimit,
  disabled = false
}) => {
  const [limit, setLimit] = useState();
  const [expanded, setExpanded] = useState(false);

  const presetLimitOptions = useSelector(selectSpendingLimitPresets);
  const limitOptions = formatPresetSpendingLimits(presetLimitOptions);

  const handleLimitChange = event => {
    const limit = event.target.value;

    setLimit(limit);

    if (limit === CUSTOM_VALUE) {
      onSpendingLimitChange('');
    } else {
      onSpendingLimitChange(limit);
    }
  };

  const handleCustomLimitChange = event => {
    const customLimit = event.target.value;
    setCustomLimit(customLimit);
    onSpendingLimitChange(customLimit);
  };

  const maxLimitMessage =
    customLimit > 0
      ? 'Max spending limit is $500'
      : 'Spending limit cannot be negative';

  const handleExpandMore = () => {
    if (!disabled) {
      setExpanded(!expanded);
    }
  };

  return (
    <SpendingLimitContainer>
      <TitleWrapper>
        <CreditCardIcon />
        <TitleTypography>{SPENIDNG_LIMIT_TITLE}</TitleTypography>
        {expanded ? (
          <ExpandLessIcon
            onClick={() => setExpanded(!expanded)}
            sx={{ cursor: 'pointer' }}
          />
        ) : (
          <ExpandMoreIcon
            onClick={handleExpandMore}
            sx={disabled ? { cursor: 'not-allowed' } : { cursor: 'pointer' }}
          />
        )}
      </TitleWrapper>
      <BudgetTypography>{SPENDING_LIMIT_BUDGET_STRING}</BudgetTypography>
      {expanded && (
        <>
          <RadioGroup
            list={limitOptions}
            value={limit}
            onChange={handleLimitChange}
            row
          >
            {limitOptions.map((option, index) => (
              <GroupOrderSpendingOption
                key={option.value}
                option={option}
                isSelected={limit === option.value}
                disabled={disabled}
                control={<Radio />}
              />
            ))}
            <FormControlLabel
              value={CUSTOM_VALUE}
              control={<Radio />}
              label="Custom"
              disabled={disabled}
            />
          </RadioGroup>
          {limit === CUSTOM_VALUE && (
            <CustomInput
              error={!isCustomLimitValid}
              helperText={isCustomLimitValid ? null : maxLimitMessage}
              type="number"
              placeholder="0"
              value={customLimit}
              onChange={handleCustomLimitChange}
              InputProps={{
                startAdornment: <DollarIcon position="start">$</DollarIcon>,
                inputProps: {
                  min: 0,
                  max: 500
                }
              }}
            />
          )}
        </>
      )}
    </SpendingLimitContainer>
  );
};

GroupOrderSpendingLimit.propTypes = {
  onSpendingLimitChange: PropTypes.func.isRequired,
  setCustomLimit: PropTypes.func.isRequired,
  isCustomLimitValid: PropTypes.bool.isRequired,
  customLimit: PropTypes.number,
  disabled: PropTypes.bool.isRequired
};

export default GroupOrderSpendingLimit;
